/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import $ from '@/jquery'
import { createIcon } from '@/io.ox/core/components'
import gt from 'gettext'
import HelpApp from '@/io.ox/help/main'
import Backbone from '@/backbone'
import ox from '@/ox'
import _ from '@/underscore'
import SidepanelToolbar from '@/io.ox/core/sidepanel/toolbar.vue'
import { createApp, ref } from 'vue'
import { isEnabledByUser, onChangeUserToggle } from '@/io.ox/core/feature'

const SidepanelView = Backbone.View.extend({
  el: '#io-ox-sidepanel',
  events: {
    'click .back': 'navigateBack',
    'click .help-button': 'openHelp',
    'click .close-button': 'hide',
    'mousedown .resizebar': 'onResize'
  },
  initialize () {
    this.widgets = {}
    this.activeWidget = ref(null)
    this.activeWidgetGroup = ref(null)
    if (!_.device('smartphone')) {
      this.$el.append($('<div class="resizebar" style="left: calc(-1 * var(--gap))">'))
    }
  },
  isVisible () {
    return $('#io-ox-core').hasClass('sidepanel-visible')
  },
  toggle (state) {
    $('#io-ox-core').toggleClass('sidepanel-visible', state ?? !this.isVisible())
    this.trigger('toggle', state, this.activeWidget.value)
    if (!state) {
      this.activeWidget.value = null
      this.activeWidgetGroup.value = null
    }
  },
  hide () {
    this.toggle(false)
    this.trigger('hide')
  },
  show () {
    this.toggle(true)
    this.trigger('show', this.activeWidget.value)
  },
  openHelp () {
    if (HelpApp.reuse({ base: 'help', href: this.activeWidget.value.helpUrl })) return
    HelpApp.getApp({ base: 'help', href: this.activeWidget.value.helpUrl }).launch()
  },
  navigateBack () {
    const back = this.activeWidget.value.back
    if (back) this.makeActive(back)
  },
  getActive () {
    return this.activeWidget.value?.id
  },
  getActiveRef () {
    return this.activeWidget
  },
  getActiveGroupRef () {
    return this.activeWidgetGroup
  },
  toggleActive (id) {
    if (this.getActive() === id && this.isVisible()) this.hide()
    else this.makeActive(id)
  },
  makeActive (id) {
    const widget = this.widgets[id]
    if (!widget) return
    Object.values(this.widgets).forEach(widget => widget.$el.removeClass('active'))
    widget.$el.addClass('active')
    this.activeWidget.value = widget
    this.activeWidgetGroup.value = widget.group
    this.show()
    return widget
  },
  add (id, title = '', { back, $controls, helpUrl, elementId = '', group = id } = {}) {
    if (!id) return
    // avoid duplicates
    if (this.widgets[id]) return this.widgets[id]
    const $title = $('<h2 class="title truncate">')
    const $content = $('<div class="sidepanel-content">')
      // add ID to support e2e tests especially when dealing with shadow-DOM
      .attr('id', elementId || (id.replace(/([A-Z])/g, '-$1').toLowerCase() + '-sidepanel'))
    const $el = $('<div class="sidepanel" class="region">')
      .toggleClass('slidein', !!back)
      .append(
        $('<div class="sidepanel-header">').append(
          // back button
          back
            ? $('<button type="button" class="btn back">')
              .attr('title', gt('Back'))
              .append(createIcon('bi/chevron-left.svg'))
            : $(),
          // title
          $title.text(title),
          // optional controls
          $controls || $(),
          // optional help button
          helpUrl
            ? $('<button type="button" class="btn help-button">')
              .attr('title', gt('Online help'))
              .append(createIcon('bi/question-circle.svg'))
            : $(),
          // close button
          $('<button type="button" class="btn close-button">')
            // #. used as a verb
            .attr('title', gt('Close'))
            .append(createIcon('bi/x-lg.svg'))
        ),
        $content
      )
    const widget = { id, $el, $content, $title, title, back, $controls, helpUrl, group }
    this.widgets[id] = widget
    this.$el.append($el)
    return widget
  },
  remove (id) {
    const widget = this.widgets[id]
    if (!widget) return
    widget.$el.remove()
    if (widget === this.activeWidget.value) {
      this.activeWidget.value = null
      this.activeWidgetGroup.value = null
    }
    delete this.widgets[id]
    this.trigger('remove', id)
    this.hide()
  },
  onResize (e) {
    e.preventDefault()
    const el = this.$el[0]
    const rect = el.getBoundingClientRect()
    $(document).on({
      'mousemove.resize' (e) {
        const width = Math.min(960, Math.max(320, rect.right - e.pageX))
        el.style.width = `${width}px`
      },
      'mouseup.resize' () {
        $(this).off('mousemove.resize mouseup.resize')
        $(document).trigger('resize')
      }
    })
  }
})

function adjustHeightForOnScreenKeyboard () {
  const height = Math.max(0, window.innerHeight - window.visualViewport.height)
  // adjust the windowmanager pane as well to avoid clicks outside the sidepanel
  $('#io-ox-sidepanel, #io-ox-windowmanager-pane').css('bottom', `${height}px`)
}

let toolbar

// strange duplication behavior otherwise if imported in browser console
if (!ox.sidepanelView) {
  ox.sidepanelView = new SidepanelView()
  // ios specific code to handle the on screen keyboard (android doesn't need this and it causes issues)
  if (_.device('ios')) {
    // ?. is required for unit testing (all browsers actually do know it)
    window.visualViewport?.addEventListener('resize', () => {
      // nothing to do if the sidepanel is closed
      if (!ox.sidepanelView.isVisible()) return
      // while testing, the height adjustment happened during 20 and 40ms
      // sometimes the very final height change happened "later"
      // therefore the 200ms to catch a potential late change
      for (const delay of [20, 40, 60, 80, 120, 200]) {
        setTimeout(adjustHeightForOnScreenKeyboard, delay)
      }
    })
    // safety mechanism for overscrolling
    const mainNode = $('html')[0]
    $(document).on('scroll', () => { mainNode.scrollTop = 0 })
  }

  const $sidepanelToolbarContainer = $('#io-ox-sidepanel-toolbar')
    // #. just used as aria-label for screen-readers describing available sidebars (e.g. calendar, tasks, updates) on the very right side of the UI
    .attr({ role: 'region', 'aria-label': gt('Open additional sidebars') })
  ox.sidepanelView.$el
    .attr({ role: 'region', 'aria-label': gt('Sidebar') })
  const activeGroupRef = ox.sidepanelView.getActiveGroupRef()
  const app = createApp(SidepanelToolbar, { activeGroupRef })
  toolbar = app.mount($sidepanelToolbarContainer[0])
  toggleToolbar(isEnabledByUser('sidepanelToolbar'))
  onChangeUserToggle('sidepanelToolbar', value => toggleToolbar(value))

  function toggleToolbar (state) {
    $sidepanelToolbarContainer.toggle(state)
    $('#io-ox-core').toggleClass('sidepanel-toolbar-visible', state)
    if (!state) ox.sidepanelView.hide()
  }
}

export const sidepanelView = ox.sidepanelView

export function addSidepanelToolbarButton ({ group, icon, index, onClick, onIconLoad, requires, title } = {}) {
  return toolbar?.addButton({ group, icon, index, onClick, onIconLoad, requires, title })
}
